<template>
  <div
    class="cart-item"
    :class="{
      highlighted: isHighlighted,
      'free-text-cart-item': isFreeText,
      'alternative-cart-item': localIsAlternativePosition,
      'cart-item-dragging': isDragging,
      'cart-item-drop-before': dropPlacement === 'before',
      'cart-item-drop-after': dropPlacement === 'after'
    }"
    @dragover.prevent="handleDragOver"
    @drop.prevent="handleDrop"
  >
    <!-- <div class="image-container">
      <img :src="cartImage" alt="Produktbild" class="product-image">
    </div> -->
    <div class="product-details">
      <div class="left">
        <div class="one-line">
          <div class="header-value">
            <div :class="{ 'hidden-header' : pos > 1}" class="header">Pos.</div>
            <div>{{ formattedPosition }}</div>
          </div>
          <div class="header-value">
            <div :class="{ 'hidden-header' : pos > 1}" class="header">Raum</div>
            <input
              v-if="canEditFreeTextDetails"
              type="text"
              class="free-text-position-input"
              v-model="localPositionBez"
              maxlength="7"
              @change="emitFreeTextDetailsChange"
            >
            <div v-else>{{ positionBez }}</div>
          </div>
          <div class="header-value">
            <div :class="{ 'hidden-header' : pos > 1}" class="header"></div>
            <div class="image-container" :class="{ 'image-container-placeholder': isFreeText }">
              <img v-if="!isFreeText" :src="cartImage" alt="Produktbild" class="product-image">
            </div>
          </div>
          <div class="header-value">
            <div :class="{ 'hidden-header' : pos > 1}" class="header"></div>
            <div class="pos-text-container">
              <textarea
                v-if="canEditFreeTextDetails"
                class="free-text-content-input"
                :class="{ 'free-text-required-input': isLocalFreeTextEmpty }"
                v-model="localFreeTextContent"
                rows="3"
                maxlength="500"
                @change="emitFreeTextDetailsChange"
              ></textarea>
              <div v-else-if="isFreeText" class="free-text-content">{{ freeTextContent }}</div>
              <template v-else>
                <div id="article-search-text">{{ articleSearchText }}</div>
                <div class="items-grid">
                  <div class="grid-row" v-for="item in visibleItems" :key="item.id">
                    <span class="item-name">{{ item.name }}</span>
                    <span class="item-value">{{ getDisplayItemValue(item) }}<span v-if="item.hex" class="ral-dot" :style="{ backgroundColor: item.hex }"></span></span>
                  </div>
                  <div v-if="notes" class="notes-row">
                    <span class="item-name">Notizen:</span>
                    <span class="item-value">{{ notes }}</span>
                  </div>
                </div>
              </template>
            </div>
          </div>
          <div class="header-value">
            <div :class="{ 'hidden-header' : pos > 1}" class="header">LP/€</div>
            <div v-if="canEditListPrice" class="list-price-input-wrap">
              <input
                type="number"
                class="list-price-input no-spinner"
                v-model.number="localListPrice"
                min="0"
                step="0.01"
                @input="validateListPrice"
                @change="emitListPriceChange"
                @blur="emitListPriceChange"
              >
              <span class="list-price-currency-suffix">€</span>
            </div>
            <div v-else-if="hasListPrice">
              <span>
                {{ formatPriceValue(numericPrice) }}
              </span> <!-- Zeigt den ursprünglichen Preis durchgestrichen, falls Rabatt vorhanden -->
            </div>
          </div>
          <div class="header-value">
            <div :class="{ 'hidden-header' : pos > 1}" class="header">Rabatt</div>
            <div v-if="shouldShowDiscount">
              <div v-if="canEditDiscount" class="discount-input-wrap">
                <input
                  type="number"
                  class="discount-input no-spinner"
                  v-model.number="localDiscount"
                  min="0"
                  max="100"
                  step="0.1"
                  @input="validateDiscount"
                  @change="emitDiscountChange"
                  @blur="emitDiscountChange"
                >
                <span v-if="showDiscountPercentSuffix" class="discount-percent-suffix">%</span>
              </div>
              <span v-else>
                {{ formattedDiscount }}<template v-if="showDiscountPercentSuffix">%</template>
              </span> <!-- Zeigt den ursprünglichen Preis durchgestrichen, falls Rabatt vorhanden -->
            </div>
          </div>
          <div class="header-value">
            <div :class="{ 'hidden-header' : pos > 1}" class="header dynamic-field-header">EP/€</div>
            <div class="dynamic-field" v-if="hasListPrice">
              <span>
                {{ formatPriceValue(unitNetPrice) }}
              </span> <!-- Zeigt den ursprünglichen Preis durchgestrichen, falls Rabatt vorhanden -->
            </div>
          </div>
          <div class="header-value">
            <div :class="{ 'hidden-header' : pos > 1}" class="header">Menge</div>
            <!-- <div>{{ selectedQuantity }} Stk.</div> -->
            <input 
              v-if="!isReadOnly"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              :value="formattedSelectedQuantity"
              class="quantity-input"
              :class="{ 'quantity-bold': numericSelectedQuantity > 1 }"
              @input="handleQuantityInput"
              @blur="commitQuantityChange"
            >
            <span v-else :class="{ 'quantity-bold': numericSelectedQuantity > 1 }">
              {{ formatQuantityValue(selectedQuantity) }}
            </span>
          </div>
          <div class="header-value">
            <div :class="{ 'hidden-header' : pos > 1}" class="header dynamic-field-header">GP/€</div>
            <div v-if="canEditManualTotal" class="dynamic-field manual-total-field">
              <div class="manual-total-input-wrap">
                <input
                  type="number"
                  class="no-spinner"
                  v-model.number="localManualTotalPrice"
                  :min="isFreeText ? null : 0"
                  step="0.01"
                  @input="validateManualTotal"
                  @change="emitManualTotalChange"
                  @blur="emitManualTotalChange"
                >
                <span class="manual-total-currency-suffix">€</span>
              </div>
            </div>
            <div class="dynamic-field" v-else-if="hasManualTotalPrice" style="text-align: right; width: 100%;">
              <span>{{ formatPriceValue(lineTotal) }}</span>
            </div>
            <div class="dynamic-field" v-else-if="hasListPrice" style="text-align: right; width: 100%;">
              <span>{{ formatPriceValue(lineTotal) }}</span>
            </div>
            <div v-else>
              Preis auf Anfrage
            </div>
          </div>
        </div>
      </div>
      <div class="right">
        <div class="content">
          <div v-if="!isReadOnly" class="position-order-controls">
            <button
              type="button"
              class="drag-handle-button"
              :draggable="!isOrderSaving"
              :disabled="isOrderSaving"
              title="Position verschieben"
              aria-label="Position mit der Maus verschieben"
              @dragstart="handleDragStart"
              @dragend="handleDragEnd"
            >
              <i class="fa fa-grip-vertical"></i> Verschieben
            </button>
            <div class="position-order-buttons">
              <button
                type="button"
                title="Position nach oben verschieben"
                aria-label="Position nach oben verschieben"
                :disabled="!canMoveUp || isOrderSaving"
                @click="moveItem('up')"
              >
                <i class="fa fa-chevron-up"></i>
              </button>
              <button
                type="button"
                title="Position nach unten verschieben"
                aria-label="Position nach unten verschieben"
                :disabled="!canMoveDown || isOrderSaving"
                @click="moveItem('down')"
              >
                <i class="fa fa-chevron-down"></i>
              </button>
            </div>
          </div>
          <button
            v-if="!isReadOnly && (!isFreeText || isInternalAdmin)"
            class="copy-button"
            @click="copyItem"
          >
            <i class="fa fa-copy"></i> Kopieren
          </button>
          <button v-if="!isFreeText && !isReadOnly" class="open-button" @click="openItem">
            <i class="fa fa-folder-open"></i> Bearbeiten
          </button>
          <button v-if="!isReadOnly" class="delete-button" @click="removeItem()">
            <i class="fa fa-trash"></i> Löschen
          </button>
          <label v-if="!isReadOnly" class="alternative-position-toggle">
            <input
              type="checkbox"
              :checked="localIsAlternativePosition"
              @change="emitAlternativePositionChange"
            >
            <span>Alternativposition</span>
          </label>
          <div v-else-if="localIsAlternativePosition" class="alternative-position-badge">
            Alternativposition
          </div>
        </div>
      </div>
    </div>
    <div class="cart-item-border"></div>
    <div v-if="isInternalAdmin && !isReadOnly" class="cart-item-insert">
      <button
        type="button"
        class="insert-free-text-button"
        title="Freitextposition hier einfügen"
        aria-label="Freitextposition hier einfügen"
        @click="insertFreeTextAfter"
      >
        +
      </button>
    </div>
  </div>
</template>

<script>
import api from '@/api/client';
import { formatDiscount, formatInteger, formatPrice, normalizePositiveInteger } from '@/utils/numberFormat';

const imageCache = new Map();
const inFlightImages = new Map();
const cacheKeyFor = (articleId, priority) => `${articleId}__${priority}`;
const normalizeQuantityValue = (value, fallback = 1) => normalizePositiveInteger(value, fallback);
const getFreeTextValue = (items = [], notes = '') => {
  const itemWithValue = (items || []).find(item => {
    const value = item?.value != null && String(item.value).trim() !== ''
      ? item.value
      : item?.emptyValue;
    return value != null && String(value).trim() !== '';
  });

  if (itemWithValue) {
    return itemWithValue.value != null && String(itemWithValue.value).trim() !== ''
      ? itemWithValue.value
      : (itemWithValue.emptyValue ?? '');
  }

  return notes || '';
};

export default {
  name: 'CartItem',
  props: {
    pos: Number,
    id: Number,
    articleId: Number,
    positionBez: String,
    articleSearchText: String,
    priorityFolders: String,
    items: Array,
    price: Number,
    originalPrice: {
      type: Number,
      default: null
    },
    manualListPrice: {
      type: Number,
      default: null
    },
    productImage: String,
    amount: Number,
    notes: String,
    discount: { 
      type: Number,
      default: 0.0
    },
    manualTotalPrice: {
      type: Number,
      default: null
    },
    isFreeText: {
      type: Boolean,
      default: false
    },
    isAlternativePosition: {
      type: Boolean,
      default: false
    },
    isInternalAdmin: {
      type: Boolean,
      default: false
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    canMoveUp: {
      type: Boolean,
      default: false
    },
    canMoveDown: {
      type: Boolean,
      default: false
    },
    isOrderSaving: {
      type: Boolean,
      default: false
    },
    isDragging: {
      type: Boolean,
      default: false
    },
    dropPlacement: {
      type: String,
      default: null
    },
  },
  data() {
    return {
      selectedQuantity: normalizeQuantityValue(this.amount),
      localListPrice: this.getInitialListPrice(),
      localDiscount: this.discount || 0,
      localManualTotalPrice: this.manualTotalPrice,
      localPositionBez: this.positionBez || '',
      localFreeTextContent: getFreeTextValue(this.items, this.notes),
      localIsAlternativePosition: !!this.isAlternativePosition,
      cartImage: "",
      isHighlighted: false,
      highlightTimer: null,
    };
  },
  computed: {
    formattedPosition() {
      if (this.pos == null || isNaN(Number(this.pos))) {
        return '--';
      }

      const numPos = Number(this.pos);
      return numPos < 10 ? `0${numPos}` : `${numPos}`;
    },
    formattedDiscount() {
      let val = Number(this.localDiscount);
      if (isNaN(val)) val = 0;
      return formatDiscount(val);
    },
    formattedSelectedQuantity() {
      if (this.isEmptyNumberInput(this.selectedQuantity)) {
        return '';
      }

      return this.formatQuantityValue(this.selectedQuantity);
    },
    numericSelectedQuantity() {
      if (this.isEmptyNumberInput(this.selectedQuantity)) {
        return 0;
      }

      return normalizeQuantityValue(this.selectedQuantity, 0);
    },
    numericPrice() {
      if (this.canEditListPrice) {
        return this.manualListPriceNumber ?? 0;
      }

      const price = Number(this.price);
      return Number.isFinite(price) ? price : 0;
    },
    originalPriceNumber() {
      const price = Number(this.originalPrice ?? this.price);
      return Number.isFinite(price) ? price : 0;
    },
    manualListPriceNumber() {
      if (this.isEmptyNumberInput(this.localListPrice)) {
        return null;
      }

      const value = Number(String(this.localListPrice).replace(',', '.'));
      return Number.isFinite(value) && value > 0 ? value : null;
    },
    storedManualListPriceNumber() {
      if (this.isEmptyNumberInput(this.manualListPrice)) {
        return null;
      }

      const value = Number(String(this.manualListPrice).replace(',', '.'));
      return Number.isFinite(value) && value > 0 ? value : null;
    },
    hasListPrice() {
      return this.numericPrice > 0;
    },
    hasOriginalListPrice() {
      return this.originalPriceNumber > 0;
    },
    canEditListPrice() {
      return this.isInternalAdmin
        && !this.isReadOnly
        && (this.isFreeText || !this.hasOriginalListPrice || this.storedManualListPriceNumber !== null);
    },
    canEditDiscount() {
      return this.isInternalAdmin
        && !this.isReadOnly
        && (this.isFreeText || this.hasListPrice || this.canEditListPrice);
    },
    shouldShowDiscount() {
      return this.isFreeText || this.hasListPrice || this.canEditListPrice;
    },
    showDiscountPercentSuffix() {
      return true;
    },
    manualTotalNumber() {
      if (this.localManualTotalPrice === null || this.localManualTotalPrice === undefined || this.localManualTotalPrice === '') {
        return null;
      }

      const value = Number(String(this.localManualTotalPrice).replace(',', '.'));
      return Number.isFinite(value) ? value : null;
    },
    hasManualTotalPrice() {
      if (this.manualTotalNumber === null) {
        return false;
      }

      return !this.hasListPrice && this.manualTotalNumber >= 0;
    },
    canEditManualTotal() {
      return false;
    },
    canEditFreeTextDetails() {
      return this.isFreeText && this.isInternalAdmin && !this.isReadOnly;
    },
    isLocalFreeTextEmpty() {
      return !String(this.localFreeTextContent || '').trim();
    },
    unitNetPrice() {
      const discount = this.getEffectiveDiscount();
      return this.numericPrice * (1 - discount / 100);
    },
    lineTotal() {
      if (this.hasManualTotalPrice) {
        return this.manualTotalNumber;
      }
      if (!this.hasListPrice) {
        return null;
      }
      return this.unitNetPrice * this.numericSelectedQuantity;
    },
    visibleItems() {
      return (this.items || []).filter(item => !this.shouldHideItem(item));
    },
    freeTextContent() {
      return getFreeTextValue(this.items, this.notes);
    }
  },
  methods: {
    getInitialListPrice() {
      if (!this.isEmptyNumberInput(this.manualListPrice)) {
        return this.manualListPrice;
      }

      return '';
    },
    formatPriceValue(value) {
      return formatPrice(value);
    },
    formatQuantityValue(value) {
      return formatInteger(value);
    },
    async loadImageForCart() {
      if (this.isFreeText) {
        this.cartImage = '';
        return '';
      }

      if (this.productImage) {
        this.cartImage = this.productImage;
        return this.productImage;
      }

      try {
        const parts = (this.articleSearchText || '').split(' - ');
        const articleId = (parts[0] || '').trim();
        if (!articleId) {
          this.cartImage = '';
          return '';
        }

        const providedPriority = (this.priorityFolders || '').trim();
        let priorityFolders = providedPriority || 'ohne';

        // Fallback-Heuristik nur, wenn kein Priority-Flag gespeichert wurde
        if (!providedPriority) {
          let kaempferRechts = false;
          let kaempferRechtsXL = false;
          let kaempferHoehe = false;
          let kaempferHoeheXL = false;

          for (let i = 0; i < this.items.length; ++i) {
            // Contains check (case-insensitive) - erkennt "Kämpfer von links", "kämpfer links", etc.
            if (this.items[i].name.toLowerCase().includes('kämpfer') && 
                this.items[i].name.toLowerCase().includes('links')) {
              kaempferRechts = true;
              if (this.items[i].value.toLowerCase().includes('xl')) {
                kaempferRechtsXL = true; 
              }
            }
            
            if (this.items[i].name.toLowerCase().includes('kämpfer') && 
                this.items[i].name.toLowerCase().includes('höhe')) {
              kaempferHoehe = true;
              if (this.items[i].value.toLowerCase().includes('xl')) {
                kaempferHoeheXL = true; 
              }
            }
          }

          if (kaempferRechtsXL) {
            priorityFolders = 'XLquer';
          }
          else if (kaempferHoeheXL) {
            priorityFolders = 'XLhoch';
          }
          else if (kaempferRechts && kaempferHoehe) {
            priorityFolders = 'KK';
          }
          else if (kaempferRechts) {
            priorityFolders = 'KS';
          }
          else if (kaempferHoehe) {
            priorityFolders = 'KW';
          }
        }

        const cacheKey = cacheKeyFor(articleId, priorityFolders);

        // Direkt aus Cache bedienen, um doppelte Requests zu vermeiden
        if (imageCache.has(cacheKey)) {
          const cached = imageCache.get(cacheKey) || '';
          this.cartImage = cached;
          if (cached) {
            this.$emit('image-loaded', {
              id: this.id,
              newPath: cached
            });
          }
          return cached;
        }

        // Parallel laufende Requests zum selben Bild wiederverwenden
        if (inFlightImages.has(cacheKey)) {
          const cached = await inFlightImages.get(cacheKey);
          this.cartImage = cached || '';
          if (cached) {
            this.$emit('image-loaded', {
              id: this.id,
              newPath: cached
            });
          }
          return cached || '';
        }

        let firstUrl = '';
        const requestPromise = (async () => {
          const safeId = (articleId ?? '').replace(/\//g, '');
          const res = await api.get(`/api/images/${safeId}`, {
            params: { priority: priorityFolders },
            validateStatus: s => s >= 200 && s < 500 // 404 etc. nicht als harter Fehler
          });

          if (res.status === 200 && Array.isArray(res.data.images) && res.data.images.length > 0) {
            return "https://p684534.mittwaldserver.info" + res.data.images[0].url;
          }
          return '';
        })();

        inFlightImages.set(cacheKey, requestPromise);

        try {
          firstUrl = await requestPromise;
        } finally {
          imageCache.set(cacheKey, firstUrl);
          inFlightImages.delete(cacheKey);
        }

        this.cartImage = firstUrl;
        if (firstUrl) {
          this.$emit('image-loaded', {
            id: this.id,
            newPath: firstUrl
          });
        } else {
          this.cartImage = '';
        }
        return firstUrl;
      } catch (error) {
        console.error('Fehler beim Laden des ersten Bildes:', error);
        this.cartImage = '';
        return '';
      }
    },
    parseSMassDisplayValue(displayValue) {
      if (!displayValue || typeof displayValue !== 'string') {
        return { s: null, groupText: null, smk: null, fa: null };
      }

      const text = displayValue.trim();

      const re = /^\s*(\d+)\s*mm(?:\s*\(([^)]+)\))?(?:\s+(\d+)\s*mm)?(?:\s+(\d+)\s*mm)?\s*$/i;
      const m = text.match(re);

      if (!m) {
        return { s: null, groupText: null, smk: null, fa: null };
      }

      const [, s, groupText, smk, fa] = m;

      return {
        s: s != null ? Number(s) : null,
        groupText: groupText || null,
        smk: smk != null ? Number(smk) : null,
        fa: fa != null ? Number(fa) : null,
      };
    },
    parseFluegelDisplayValue(displayValue) {
      const nums = Array.from(String(displayValue || '').matchAll(/(\d+(?:[.,]\d+)?)/g)).map(m => m[1].replace(',', '.'));
      if (nums.length === 0) {
        return { left: null, right: null, height: null };
      }

      const left = nums[0] ?? null;
      const right = nums.length >= 3 ? nums[2] : (nums[1] ?? null);
      const height = nums.length >= 2 ? nums[1] : null;

      return { left, right, height };
    },
    normalizeDimensionValue(val) {
      return (val ?? '').toString().replace(/mm/ig, '').replace(/,/g, '.').trim();
    },
    getHeightFromItems() {
      const dimItem = (this.items || []).find(it => {
        const n = (it.name || '').toLowerCase();
        return n.includes('maß') && !n.includes('s-maß');
      });
      if (dimItem && typeof dimItem.value === 'string') {
        const parsedDim = this.parseFluegelDisplayValue(dimItem.value);
        if (parsedDim.height) {
          return this.normalizeDimensionValue(parsedDim.height);
        }
        if (parsedDim.right) {
          return this.normalizeDimensionValue(parsedDim.right);
        }
      }
      return null;
    },

    getRawItemValue(item) {
      return item.value != null && String(item.value).trim() !== ''
        ? item.value
        : (item.emptyValue != null ? item.emptyValue : item.value);
    },
    shouldHideItem(item) {
      const rawValue = this.getRawItemValue(item);
      return typeof rawValue === 'string' && rawValue.trim().toLowerCase() === 'ohne';
    },
    formatGriffkaempferHoeheValue(item, rawValue) {
      const name = (item?.name || '').toLowerCase();
      const value = String(rawValue || '').trim();

      if (!name.includes('kämpfer') || !name.includes('höhe') || !value || value.toLowerCase().includes('v. uk ram')) {
        return null;
      }

      const numericValue = Number(value.replace(/mm/ig, '').replace(',', '.').trim());
      if (Number.isNaN(numericValue)) {
        return null;
      }

      return `${this.normalizeDimensionValue(value)}mm v. UK RAM`;
    },
    formatEndrasterwunschhoeheValue(item, rawValue) {
      const name = (item?.name || '').toLowerCase();
      const value = String(rawValue || '').trim();

      if (name !== 'endrasterwunschhöhe' || !value || value.toLowerCase().includes('v. unten')) {
        return null;
      }

      const numericValue = Number(value.replace(/mm/ig, '').replace(',', '.').trim());
      return Number.isNaN(numericValue) ? null : `${this.normalizeDimensionValue(value)}mm v. unten`;
    },
    formatLabeledLineValue(rawValue) {
      if (typeof rawValue !== 'string' || !/(^|\s)Seite\s+\d+\s*:/i.test(rawValue)) {
        return null;
      }

      const parts = rawValue
        .split(/\s+\/\s+|\n+/)
        .map(part => part.trim())
        .filter(Boolean);

      if (parts.length < 2 || !parts.every(part => /^Seite\s+\d+\s*:/i.test(part))) {
        return null;
      }

      return parts.join('\n');
    },

    getDisplayItemValue(item) {
      const rawValue = this.getRawItemValue(item);

      if (item.name === 'S-Maß' && typeof rawValue === 'string') {
        const parsed = this.parseSMassDisplayValue(rawValue);

        if (parsed?.smk != null && parsed?.groupText) {
          const groupNumberMatch = parsed.groupText.match(/\d+/);
          const groupNumber = groupNumberMatch ? groupNumberMatch[0] : parsed.groupText;
          return `S-Maß (Klammer) ${parsed.smk}mm aus Gr. ${groupNumber}`;
        }
      }

      const formattedLabeledLineValue = this.formatLabeledLineValue(rawValue);
      if (formattedLabeledLineValue) {
        return formattedLabeledLineValue;
      }

      const formattedGriffkaempferHoehe = this.formatGriffkaempferHoeheValue(item, rawValue);
      if (formattedGriffkaempferHoehe) {
        return formattedGriffkaempferHoehe;
      }

      const formattedEndrasterwunschhoehe = this.formatEndrasterwunschhoeheValue(item, rawValue);
      if (formattedEndrasterwunschhoehe) {
        return formattedEndrasterwunschhoehe;
      }

      if (item.name === 'Flügel' && typeof rawValue === 'string') {
        const parsed = this.parseFluegelDisplayValue(rawValue);
        const left = parsed.left ? this.normalizeDimensionValue(parsed.left) : null;
        const right = parsed.right ? this.normalizeDimensionValue(parsed.right) : null;
        const heightRaw = this.getHeightFromItems() || parsed.height;
        const height = heightRaw ? this.normalizeDimensionValue(heightRaw) : null;

        if (left && right && height) {
          return `Flügel 1: ${left}mm x ${height}mm
Flügel 2: ${right}mm x ${height}mm`;
        }
        if (left && height) {
          return `Flügel 1: ${left}mm x ${height}mm`;
        }
        if (left && right) {
          return `Flügel 1: ${left}mm
Flügel 2: ${right}mm`;
        }
      }

      return rawValue;
    },
    removeItem() {
      this.$emit('remove-item', this.id);
    },
    insertFreeTextAfter() {
      this.$emit('insert-free-text-after', this.id);
    },
    moveItem(direction) {
      this.$emit('move-item', { id: this.id, direction });
    },
    handleDragStart(event) {
      if (this.isOrderSaving) {
        event.preventDefault();
        return;
      }

      this.$emit('drag-start-item', { id: this.id, event });
    },
    handleDragOver(event) {
      if (this.isReadOnly || this.isOrderSaving) {
        return;
      }

      const bounds = this.$el.getBoundingClientRect();
      const placement = event.clientY > bounds.top + bounds.height / 2 ? 'after' : 'before';
      this.$emit('drag-over-item', { id: this.id, placement });
    },
    handleDrop(event) {
      if (this.isReadOnly || this.isOrderSaving) {
        return;
      }

      const bounds = this.$el.getBoundingClientRect();
      const placement = event.clientY > bounds.top + bounds.height / 2 ? 'after' : 'before';
      this.$emit('drop-on-item', { id: this.id, placement });
    },
    handleDragEnd() {
      this.$emit('drag-end-item');
    },
    copyItem() {
      const copiedItem = {
        id: this.id,
        articleId: this.articleId,
        positionBez: this.isFreeText ? this.positionBez : `${this.positionBez} (Kopie)`,
        articleSearchText: this.articleSearchText,
        priorityFolders: this.priorityFolders,
        items: (this.items || []).map(item => ({ ...item })),
        price: this.numericPrice,
        originalPrice: this.originalPrice,
        manualListPrice: this.getManualListPrice(),
        productImage: this.productImage,
        amount: this.getSelectedQuantity() ?? 1,
        notes: this.notes,
        discount: this.getEffectiveDiscount(),
        manualDiscount: this.getEffectiveDiscount(),
        manualTotalPrice: this.getManualTotalPrice(),
        isFreeText: this.isFreeText,
        isAlternativePosition: this.getAlternativePosition(),
        pos: this.pos,
      };
      this.$emit('copy-item', copiedItem);
    },
    openItem() {
      const itemToOpen = {
        id: this.id,
        articleId: this.articleId,
        positionBez: this.positionBez,
        articleSearchText: this.articleSearchText,
        priorityFolders: this.priorityFolders,
        items: [...this.items],
        price: this.numericPrice,
        originalPrice: this.originalPrice,
        manualListPrice: this.getManualListPrice(),
        productImage: this.productImage,
        amount: this.getSelectedQuantity() ?? 1,
        notes: this.notes,
        discount: this.getEffectiveDiscount(),
        manualTotalPrice: this.getManualTotalPrice(),
        isFreeText: this.isFreeText,
        isAlternativePosition: this.getAlternativePosition(),
        pos: this.pos,
      };
      this.$emit('open-item', itemToOpen);
    },
    getSelectedQuantity() {
      if (this.isEmptyNumberInput(this.selectedQuantity)) {
        return null;
      }

      return normalizeQuantityValue(this.selectedQuantity);
    },
    getEffectiveDiscount() {
      const discount = Number(this.localDiscount);
      return Number.isFinite(discount) ? discount : 0;
    },
    getDiscountSyncValue() {
      if (this.isEmptyNumberInput(this.localDiscount)) {
        return null;
      }

      return this.getEffectiveDiscount();
    },
    getManualTotalPrice() {
      return this.hasManualTotalPrice ? this.manualTotalNumber : null;
    },
    getManualListPrice() {
      return this.canEditListPrice ? this.manualListPriceNumber : null;
    },
    getAlternativePosition() {
      return !!this.localIsAlternativePosition;
    },
    getLineTotalValue() {
      return this.lineTotal;
    },
    isEmptyNumberInput(value) {
      return value === '' || value === null || value === undefined;
    },
    validateDiscount({ commitEmpty = false } = {}) {
      if (this.isEmptyNumberInput(this.localDiscount)) {
        if (commitEmpty) {
          this.localDiscount = 0;
        }
        this.$parent.refreshCartSumPrice();
        return commitEmpty ? 0 : null;
      }

      let discount = Number(this.localDiscount);
      if (!Number.isFinite(discount)) {
        discount = 0;
      }
      if (discount < 0) {
        discount = 0;
      }
      if (discount > 100) {
        discount = 100;
      }
      this.localDiscount = discount;
      this.$parent.refreshCartSumPrice();
      return discount;
    },
    validateListPrice() {
      if (this.isEmptyNumberInput(this.localListPrice)) {
        this.$parent.refreshCartSumPrice();
        return null;
      }

      let listPrice = Number(String(this.localListPrice).replace(',', '.'));
      if (!Number.isFinite(listPrice) || listPrice < 0) {
        listPrice = 0;
      }

      this.localListPrice = listPrice;
      this.$parent.refreshCartSumPrice();
      return listPrice > 0 ? listPrice : null;
    },
    emitListPriceChange() {
      const listPrice = this.validateListPrice();
      this.$emit('list-price-changed', { id: this.id, listPrice });
    },
    emitDiscountChange() {
      const discount = this.validateDiscount({ commitEmpty: true });
      this.$emit('discount-changed', { id: this.id, discount });
    },
    validateManualTotal({ commitEmpty = false } = {}) {
      if (this.isEmptyNumberInput(this.localManualTotalPrice)) {
        if (commitEmpty) {
          this.localManualTotalPrice = 0;
        }
        this.$parent.refreshCartSumPrice();
        return commitEmpty ? 0 : null;
      }

      let manualTotalPrice = Number(this.localManualTotalPrice);
      if (!Number.isFinite(manualTotalPrice) || (!this.isFreeText && manualTotalPrice < 0)) {
        manualTotalPrice = null;
      }
      this.localManualTotalPrice = manualTotalPrice;
      this.$parent.refreshCartSumPrice();
      return manualTotalPrice;
    },
    emitManualTotalChange() {
      const manualTotalPrice = this.validateManualTotal({ commitEmpty: true });
      this.$emit('manual-total-changed', { id: this.id, manualTotalPrice });
    },
    emitFreeTextDetailsChange() {
      this.$emit('free-text-details-changed', {
        id: this.id,
        positionBez: this.localPositionBez,
        content: this.localFreeTextContent,
      });
    },
    emitAlternativePositionChange(event) {
      this.localIsAlternativePosition = !!event?.target?.checked;
      this.$emit('alternative-position-changed', {
        id: this.id,
        isAlternativePosition: this.localIsAlternativePosition,
      });
    },
    quantityChanged() {
      this.selectedQuantity = normalizeQuantityValue(this.selectedQuantity);
      this.$parent.refreshCartSumPrice();
      this.$emit('quantity-changed', { id: this.id, amount: this.selectedQuantity });
    },
    handleQuantityInput(event) {
      const rawValue = event?.target?.value ?? '';
      if (!/\d/.test(String(rawValue))) {
        this.selectedQuantity = '';
        this.$parent.refreshCartSumPrice();
        return;
      }

      this.selectedQuantity = normalizeQuantityValue(rawValue);
      this.quantityChanged();
    },
    validateQuantity() {
      if (this.isEmptyNumberInput(this.selectedQuantity)) {
        this.$parent.refreshCartSumPrice();
        return;
      }

      this.selectedQuantity = normalizeQuantityValue(this.selectedQuantity);
      this.quantityChanged(); // Aktualisiert den Preis im Warenkorb
    },
    commitQuantityChange() {
      if (this.isEmptyNumberInput(this.selectedQuantity)) {
        this.selectedQuantity = 1;
        this.quantityChanged();
        return;
      }

      const normalizedQuantity = normalizeQuantityValue(this.selectedQuantity);
      if (normalizedQuantity !== this.selectedQuantity) {
        this.selectedQuantity = normalizedQuantity;
        this.quantityChanged();
      }
    },
    flashHighlight() {
      if (this.highlightTimer) {
        clearTimeout(this.highlightTimer);
        this.highlightTimer = null;
      }

      this.isHighlighted = true;
      this.highlightTimer = setTimeout(() => {
        this.isHighlighted = false;
        this.highlightTimer = null;
      }, 2200);
    }
  },
  beforeUnmount() {
    if (this.highlightTimer) {
      clearTimeout(this.highlightTimer);
    }
  },
  watch: {
    amount(newValue) {
      this.selectedQuantity = normalizeQuantityValue(newValue);
    },
    discount(newValue) {
      this.localDiscount = newValue || 0;
    },
    price() {
      if (this.isEmptyNumberInput(this.manualListPrice)) {
        this.localListPrice = '';
      }
    },
    manualListPrice(newValue) {
      if (!this.isEmptyNumberInput(newValue)) {
        this.localListPrice = newValue;
        return;
      }

      this.localListPrice = '';
    },
    manualTotalPrice(newValue) {
      this.localManualTotalPrice = newValue;
    },
    isAlternativePosition(newValue) {
      this.localIsAlternativePosition = !!newValue;
    },
    positionBez(newValue) {
      this.localPositionBez = newValue || '';
    },
    items: {
      deep: true,
      handler(newValue) {
        if (this.isFreeText) {
          this.localFreeTextContent = getFreeTextValue(newValue, this.notes);
        }
      }
    },
    notes(newValue) {
      if (this.isFreeText) {
        this.localFreeTextContent = getFreeTextValue(this.items, newValue);
      }
    }
  }
};
</script>

<style scoped src="@/styles/WarenkorbItemComponent.css"></style>
